import { apiFetch, buildQueryString } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';
import type { EntityCompanyParticipation } from '@lognai/plugin-sdk';
import { PartnerAccessLevel } from '../types/domain-enums';

export interface EntityParticipationFilter {
  entityType?: string;
  entityId?: string;
  companyId?: string;
  isOwner?: boolean;
  role?: string;
}

export type EntityParticipationFilters = ListQueryParams<EntityParticipationFilter>;

export interface EntityParticipationCreateInput {
  entityType: string;
  entityId: string;
  companyId: string;
  role: string;
  accessLevel: PartnerAccessLevel;
  permissions: string[];
}

export interface EntityParticipationUpdateInput {
  role?: string;
  accessLevel?: PartnerAccessLevel;
  permissions?: string[];
}

const BASE_URL = '/api/admin/entity-participations';

export const entityParticipationsService = {
  /**
   * List entity participations with optional filters
   */
  list: async (filters?: EntityParticipationFilters): Promise<EntityCompanyParticipation[]> => {
    const query = buildQueryString(filters as Record<string, unknown>);
    return apiFetch<EntityCompanyParticipation[]>(`${BASE_URL}${query}`);
  },

  /**
   * Get participations for a specific entity
   */
  getByEntity: async (
    entityType: string,
    entityId: string
  ): Promise<EntityCompanyParticipation[]> => {
    return apiFetch<EntityCompanyParticipation[]>(`${BASE_URL}/entity/${entityType}/${entityId}`);
  },

  /**
   * Get entities shared with a specific company
   */
  getByCompany: async (
    companyId: string,
    entityType?: string
  ): Promise<EntityCompanyParticipation[]> => {
    const query = buildQueryString({ entityType });
    return apiFetch<EntityCompanyParticipation[]>(`${BASE_URL}/company/${companyId}${query}`);
  },

  /**
   * Get a specific participation by ID
   */
  getById: async (participationId: string): Promise<EntityCompanyParticipation> => {
    return apiFetch<EntityCompanyParticipation>(`${BASE_URL}/${participationId}`);
  },

  /**
   * Add a partner to an entity
   */
  create: async (data: EntityParticipationCreateInput): Promise<EntityCompanyParticipation> => {
    return apiFetch<EntityCompanyParticipation>(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a participation
   */
  update: async (
    participationId: string,
    data: EntityParticipationUpdateInput
  ): Promise<EntityCompanyParticipation> => {
    return apiFetch<EntityCompanyParticipation>(`${BASE_URL}/${participationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Remove a partner from an entity
   */
  delete: async (participationId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${participationId}`, { method: 'DELETE' });
  },

  /**
   * Update access level for a participation
   */
  updateAccessLevel: async (
    participationId: string,
    accessLevel: PartnerAccessLevel
  ): Promise<EntityCompanyParticipation> => {
    return apiFetch<EntityCompanyParticipation>(`${BASE_URL}/${participationId}/access-level`, {
      method: 'PUT',
      body: JSON.stringify({ accessLevel }),
    });
  },

  /**
   * Update permissions for a participation
   */
  updatePermissions: async (
    participationId: string,
    permissions: string[]
  ): Promise<EntityCompanyParticipation> => {
    return apiFetch<EntityCompanyParticipation>(`${BASE_URL}/${participationId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
  },

  /**
   * Get shared entities statistics
   */
  getStats: async (
    companyId: string
  ): Promise<{
    totalSharedEntities: number;
    sharedOrders: number;
    sharedShipments: number;
    sharedInvoices: number;
    sharedWarehouses: number;
  }> => {
    return apiFetch(`${BASE_URL}/stats/${companyId}`);
  },
};
